/**
 * Scheduling API - RTK Query endpoints for schedule/timetable management
 *
 * This API slice handles all scheduling-related operations including:
 * - Draft creation and management
 * - Schedule item CRUD operations
 * - Staff fetching for scheduling
 * - Publishing operations
 */

import { rootApi } from "../../../lib/api/rootApi";
import { baendeQueryApi } from "../../baende/api/queries";

import { markMutation } from "../hooks/mutationCooldown";

export const schedulingApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Create a new schedule draft
     * Replaces: supabase.rpc('create_schedule_draft_react')
     * DB Signature: p_title text, p_semester_id uuid, p_notes text DEFAULT NULL
     */
    createScheduleDraft: builder.mutation<
      any,
      {
        p_title: string;
        p_semester_id: string;
        p_notes?: string | null;
      }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "create_schedule_draft_react",
        params,
      }),
      invalidatesTags: ["Timetable"],
    }),

    /**
     * Fetch the ultimate single-payload for the Stundenplan.
     * Replaces ~20 individual network waterfalls by fetching all reference,
     * staff, and schedule data in one massive JSON object.
     */
    getInitialSchedulingPayload: builder.query<
      any,
      { p_draft_id?: string; p_semester_id?: string; schoolId: string }
    >({
      query: (params) => {
        // We strip schoolId since it's just for cache differentiation
        const { schoolId, ...rpcParams } = params;
        return {
          type: "rpc",
          rpc: "fn_scheduling_get_initial_payload",
          params: rpcParams,
        };
      },
      providesTags: ["Timetable", "DraftLessons", "Staff"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.baende_data?.configured_bands) return;
          // Seed the baende cache so BandAssignmentPanel/LessonBandSection don't fire fn_baende_get_for_school
          dispatch(
            baendeQueryApi.util.upsertQueryData(
              "getBaendeForSchool",
              undefined as any,
              data.baende_data.configured_bands,
            ),
          );
        } catch {
          // Payload failed — baende components will fetch on their own
        }
      },
    }),

    /**
     * Get draft lessons for scheduling
     * Replaces: supabase.rpc('scheduling_get_draft_lessons_react')
     */
    getDraftLessons: builder.query<
      any,
      {
        p_draft_id: string;
        p_school_id: string; // kept for interface compatibility but not sent to RPC
      }
    >({
      query: ({ p_draft_id }) => ({
        type: "rpc",
        rpc: "scheduling_get_draft_lessons_react",
        params: { p_draft_id },
      }),
      providesTags: ["Timetable", "DraftLessons"],
    }),

    /**
     * Fetch staff for scheduling
     * Replaces: supabase.rpc('fetch_staff_for_scheduling_react')
     */
    getStaffForScheduling: builder.query<
      any,
      {
        p_school_id: string;
      }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "fetch_staff_for_scheduling_react",
        params,
      }),
      providesTags: ["Staff"],
    }),

    /**
     * Create a new schedule item
     * Replaces: supabase.rpc('create_schedule_item_react')
     * DB Signature: p_class_id, p_day_id, p_draft_id, p_end_date, p_end_time, p_meeting_name, p_notes,
     *               p_period_id, p_primary_teacher_id, p_room_id, p_start_date, p_start_time, p_subject_id, p_teacher_ids
     */
    createScheduleItem: builder.mutation<
      any,
      {
        p_draft_id?: string | null;
        p_class_id?: string | null;
        p_course_id?: string | null;
        p_primary_teacher_id?: string | null;
        p_subject_id?: string | null;
        p_room_id?: string | null;
        p_period_id?: string | null;
        p_day_id?: number | null;
        p_start_date?: string | null;
        p_end_date?: string | null;
        p_start_time?: string | null;
        p_end_time?: string | null;
        p_notes?: string | null;
        p_meeting_name?: string | null;
        p_teacher_ids?: string[];
        p_item_type?: string | null;
        p_target_class_ids?: string[];
        p_is_deputat?: boolean;
        p_exclude_holidays?: boolean;
        p_rrule?: string | null;
        p_category_id?: string | null;
        p_span_blocks?: number;
        /** Frontend-only: school_id for cache key matching. Stripped before RPC call. */
        _school_id?: string;
      }
    >({
      query: ({ _school_id, ...params }) => ({
        type: "rpc",
        rpc: "create_schedule_item_react",
        params,
      }),
      // Only invalidate if _school_id not provided (fallback for callers not yet updated)
      invalidatesTags: (result, error, arg) =>
        arg._school_id ? [] : ["DraftLessons"],
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        if (!params._school_id || !params.p_draft_id) return;
        try {
          const { data } = await queryFulfilled;
          if (!data?.success || !data?.id) return;

          markMutation();
          // Patch the getDraftLessons cache with the new item
          dispatch(
            schedulingApi.util.updateQueryData(
              "getDraftLessons",
              {
                p_draft_id: params.p_draft_id,
                p_school_id: params._school_id,
              },
              (draft: any[]) => {
                draft.push({
                  id: data.id,
                  draft_id: params.p_draft_id,
                  course_id: params.p_course_id || data.course_id || null,
                  subject_id: params.p_subject_id || null,
                  class_id: params.p_class_id || null,
                  room_id: params.p_room_id || null,
                  teacher_ids: params.p_teacher_ids || [],
                  primary_teacher_id: params.p_primary_teacher_id || null,
                  day_id: params.p_day_id ?? null,
                  period_id: params.p_period_id || null,
                  start_time: params.p_start_time || null,
                  end_time: params.p_end_time || null,
                  start_date: params.p_start_date || null,
                  end_date: params.p_end_date || null,
                  meeting_name: params.p_meeting_name || null,
                  notes: params.p_notes || null,
                  school_id: params._school_id,
                  created_at: new Date().toISOString(),
                  subject_name: "Unknown Subject",
                  class_name: "Unknown Class",
                  room_name: "Unknown Room",
                  primary_teacher_name: "Unknown Teacher",
                  teacher_names: ["Unknown Teacher"],
                  block_number: 1,
                  schedule_type:
                    params.p_item_type && params.p_item_type !== "lesson"
                      ? params.p_item_type
                      : params.p_course_id
                      ? "course"
                      : "lesson",
                  item_type: params.p_item_type || "lesson",
                  target_class_ids: params.p_target_class_ids || null,
                  is_deputat: params.p_is_deputat,
                  exclude_holidays: params.p_exclude_holidays,
                  category_id: params.p_category_id ?? null,
                  // rrule is server-computed for termin items; take from the RPC response
                  rrule: data?.rrule ?? params.p_rrule ?? null,
                  span_blocks: data?.span_blocks ?? params.p_span_blocks ?? 1,
                });
              },
            ),
          );
        } catch {
          // Mutation failed — no cache update, tag invalidation handles recovery
        }
      },
    }),

    /**
     * Update an existing schedule item
     * Replaces: supabase.rpc('update_schedule_item_react')
     * DB Signature: p_item_id, p_subject_id, p_class_id, p_room_id, p_primary_teacher_id, p_teacher_ids, p_notes, p_meeting_name
     */
    updateScheduleItem: builder.mutation<
      any,
      {
        p_item_id: string;
        p_subject_id?: string | null;
        p_class_id?: string | null;
        p_room_id?: string | null;
        p_primary_teacher_id?: string | null;
        p_teacher_ids?: string[] | null;
        p_notes?: string | null;
        p_meeting_name?: string | null;
        p_item_type?: string | null;
        p_target_class_ids?: string[] | null;
        p_is_deputat?: boolean;
        p_exclude_holidays?: boolean;
        p_rrule?: string | null;
        p_category_id?: string | null;
        p_clear_category?: boolean;
        p_span_blocks?: number;
        p_end_time?: string | null;
        /** Frontend-only: for cache key matching */
        _draft_id?: string;
        _school_id?: string;
      }
    >({
      query: ({ _draft_id, _school_id, ...params }) => ({
        type: "rpc",
        rpc: "update_schedule_item_react",
        params,
      }),
      invalidatesTags: (result, error, arg) =>
        arg._school_id && arg._draft_id ? [] : ["DraftLessons"],
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        if (!params._school_id || !params._draft_id) return;
        try {
          await queryFulfilled;
          markMutation();
          dispatch(
            schedulingApi.util.updateQueryData(
              "getDraftLessons",
              {
                p_draft_id: params._draft_id,
                p_school_id: params._school_id,
              },
              (draft: any[]) => {
                const idx = draft.findIndex(
                  (item) => item.id === params.p_item_id,
                );
                if (idx === -1) return;
                // Merge only the fields that were actually sent
                const item = draft[idx];
                if (params.p_subject_id !== undefined)
                  item.subject_id = params.p_subject_id;
                if (params.p_class_id !== undefined)
                  item.class_id = params.p_class_id;
                if (params.p_room_id !== undefined)
                  item.room_id = params.p_room_id;
                if (params.p_primary_teacher_id !== undefined)
                  item.primary_teacher_id = params.p_primary_teacher_id;
                if (params.p_teacher_ids !== undefined)
                  item.teacher_ids = params.p_teacher_ids;
                if (params.p_notes !== undefined) item.notes = params.p_notes;
                if (params.p_meeting_name !== undefined)
                  item.meeting_name = params.p_meeting_name;
                if (params.p_item_type !== undefined)
                  item.item_type = params.p_item_type;
                if (params.p_target_class_ids !== undefined)
                  item.target_class_ids = params.p_target_class_ids;
                if (params.p_is_deputat !== undefined)
                  item.is_deputat = params.p_is_deputat;
                if (params.p_exclude_holidays !== undefined)
                  item.exclude_holidays = params.p_exclude_holidays;
                if (params.p_clear_category) {
                  item.category_id = null;
                } else if (params.p_category_id !== undefined) {
                  item.category_id = params.p_category_id;
                }
                if (params.p_rrule !== undefined) item.rrule = params.p_rrule;
                if (params.p_span_blocks !== undefined)
                  item.span_blocks = params.p_span_blocks;
                if (params.p_end_time !== undefined)
                  item.end_time = params.p_end_time;
              },
            ),
          );
        } catch {
          // Mutation failed — no cache update
        }
      },
    }),

    /**
     * Delete a schedule item
     * Replaces: supabase.rpc('delete_schedule_item_react')
     */
    deleteScheduleItem: builder.mutation<
      any,
      {
        p_item_id: string;
        /** Frontend-only: for cache key matching */
        _draft_id?: string;
        _school_id?: string;
      }
    >({
      query: ({ p_item_id }) => ({
        type: "rpc",
        rpc: "delete_schedule_item_react",
        params: { p_item_id },
      }),
      invalidatesTags: (result, error, arg) =>
        arg._school_id && arg._draft_id ? [] : ["DraftLessons"],
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        if (!params._school_id || !params._draft_id) return;
        try {
          await queryFulfilled;
          markMutation();
          dispatch(
            schedulingApi.util.updateQueryData(
              "getDraftLessons",
              {
                p_draft_id: params._draft_id,
                p_school_id: params._school_id,
              },
              (draft: any[]) => {
                const idx = draft.findIndex(
                  (item) => item.id === params.p_item_id,
                );
                if (idx !== -1) draft.splice(idx, 1);
              },
            ),
          );
        } catch {
          // Mutation failed — no cache update
        }
      },
    }),

    /**
     * Save draft version
     * Replaces: supabase.rpc('scheduling_save_draft_version')
     */
    saveDraftVersion: builder.mutation<
      any,
      {
        p_draft_id: string;
        p_version_name?: string;
        p_created_by: string;
      }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "scheduling_save_draft_version",
        params,
      }),
      invalidatesTags: ["Timetable"],
    }),

    /**
     * Publish lessons draft
     * Replaces: supabase.rpc('publish_lessons_draft_react')
     */
    publishLessonsDraft: builder.mutation<
      any,
      {
        p_draft_id: string;
        p_conflict_strategy?: "replace" | "skip";
        p_force?: boolean;
        p_hard_reset?: boolean;
        /**
         * How to treat existing per-occurrence calendar_event overrides for
         * draft termine:
         *   'replace' — delete detached children + clear master exceptions
         *               so the regenerated recurrence is canonical.
         *   'keep'    — preserve master exceptions and leave detached children
         *               intact so per-occurrence edits survive the publish.
         * Defaults to 'replace' in the RPC.
         */
        p_override_strategy?: "replace" | "keep";
      }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "publish_lessons_draft_react",
        params,
      }),
      invalidatesTags: ["Timetable"],
    }),

    /**
     * Pre-publish: list per-termin overrides that would be affected. Returns
     * one row per draft_schedule_item whose master calendar_event has either
     * exception_dates set or detached children — i.e. the publish dialog
     * should surface these before the admin commits.
     */
    getPublishCalendarOverrideSummary: builder.query<
      Array<{
        draft_item_id: string;
        meeting_name: string;
        master_event_id: string;
        exception_dates_count: number;
        detached_children_count: number;
      }>,
      { draftId: string }
    >({
      query: ({ draftId }) => ({
        type: "rpc",
        rpc: "fn_publish_calendar_override_summary",
        params: { p_draft_id: draftId },
      }),
    }),

    /**
     * Get last edited draft
     * Replaces: scheduleDataAdapter.fetchLastEditedDraft()
     */
    getLastEditedDraft: builder.query<{ id: string; title: string; notes?: string } | null, { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "schedule_drafts",
            params: {
              select: "id, title, notes, updated_at",
              school_id: schoolId,
              order: "updated_at.desc",
              limit: 1,
            },
          });

          if (result.error) return { error: result.error };
          const data = result.data as any[];
          if (!data || data.length === 0) return { data: null };
          return {
            data: {
              id: data[0].id,
              title: data[0].title || "Unbenannter Entwurf",
              notes: data[0].notes,
            },
          };
        } catch (error) {
          return { error: error as any };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get all drafts for the school with lesson counts, semester labels, and creator names.
     * Single RPC replaces 4 separate DraftsList queries.
     */
    getDraftsList: builder.query<any[], void>({
      query: () => ({
        type: "rpc",
        rpc: "fn_scheduling_get_drafts_list",
        params: {},
      }),
      providesTags: ["Timetable"],
    }),

    /**
     * Get draft by ID
     * Replaces: scheduleDataAdapter.fetchDraftById()
     */
    getDraftById: builder.query<
      { id: string; title: string; notes?: string } | null,
      { draftId: string }
    >({
      queryFn: async ({ draftId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "schedule_drafts",
            params: {
              select: "id, title, notes, updated_at",
              id: draftId,
              single: true,
            },
          });

          if (result.error) return { data: null };
          const data = result.data as any;
          return {
            data: {
              id: data.id,
              title: data.title || "Unbenannter Entwurf",
              notes: data.notes,
            },
          };
        } catch (error) {
          return { data: null };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get live schedules. When semesterId is set, uses RPC get_live_schedules_effective(p_semester_id);
     * when null/undefined, uses vw_react_live_schedules_effective (valid today).
     */
    getLiveSchedules: builder.query<
      any[],
      { schoolId: string; semesterId?: string | null }
    >({
      queryFn: async (
        { schoolId, semesterId },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        try {
          if (semesterId != null && semesterId !== "") {
            const result = await baseQuery({
              type: "rpc",
              rpc: "get_live_schedules_effective",
              params: { p_semester_id: semesterId },
            });
            if (result.error) return { data: [] };
            return { data: (result.data as any[]) || [] };
          }

          // Valid today: try effective view first
          const result = await baseQuery({
            type: "select",
            table: "vw_react_live_schedules_effective",
            params: {
              select: "id,course_id,subject_id,class_id,room_id,primary_teacher_id,teacher_ids,day_id,day_number,period_id,block_number,start_time,end_time,meeting_name,notes,subject_name,class_name,room_name,schedule_type,item_type,target_class_ids,grade_level",
              limit: 5000,
              order: "day_number, block_number, start_time",
            },
          });

          if (result.error) {
            const fallback = await baseQuery({
              type: "select",
              table: "vw_react_live_schedules",
              params: {
                select: "id,course_id,subject_id,class_id,room_id,primary_teacher_id,teacher_ids,day_id,day_number,period_id,block_number,start_time,end_time,meeting_name,notes,subject_name,class_name,room_name,schedule_type,item_type,target_class_ids,grade_level",
                limit: 5000,
                order: "day_number, block_number, start_time",
              },
            });

            if (fallback.error) {
              const finalFallback = await baseQuery({
                type: "select",
                table: "course_schedules",
                params: {
                  select:
                    "id, course_id, subject_id, class_id, room_id, teacher_ids, day_id, period_id, start_time, end_time, start_date, end_date, meeting_name, notes, is_archived",
                  is_archived: false,
                  limit: 2000,
                },
              });

              return { data: (finalFallback.data as any[]) || [] };
            }

            return { data: (fallback.data as any[]) || [] };
          }

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get classes for scheduling
     * Replaces: scheduleDataAdapter.fetchClasses()
     */
    getClassesForScheduling: builder.query<any[], { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "structure_classes",
            params: {
              select: "id, name, grade_level, school_id, room_id",
              school_id: schoolId,
              order: "grade_level, name",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get subjects for scheduling
     * Replaces: scheduleDataAdapter.fetchSubjects()
     */
    getSubjectsForScheduling: builder.query<any[], { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "subjects",
            params: {
              select: "id, name, abbreviation, color, school_id",
              school_id: schoolId,
              order: "name",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get class-specific subject hours (subject_class_hours table).
     * Used by useAllClassSubjectHours to show required hours per class.
     */
    getSubjectClassHours: builder.query<
      any[],
      { classIds: string[]; schoolId: string }
    >({
      queryFn: async (
        { classIds, schoolId },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        if (!classIds.length || !schoolId) return { data: [] };
        try {
          const result = await baseQuery({
            type: "select",
            table: "subject_class_hours",
            params: {
              select: "subject_id, hours_per_week, class_id",
              class_id: classIds,
              school_id: schoolId,
            },
          });
          return { data: (result.data as any[]) || [] };
        } catch {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get grade-level subject hours (subject_grade_hours table).
     * Used as fallback when no class-specific hours exist.
     */
    getSubjectGradeHours: builder.query<
      any[],
      { gradeLevels: string[]; schoolId: string }
    >({
      queryFn: async (
        { gradeLevels, schoolId },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        if (!gradeLevels.length || !schoolId) return { data: [] };
        try {
          const result = await baseQuery({
            type: "select",
            table: "subject_grade_hours",
            params: {
              select: "subject_id, hours_per_week, grade_level",
              grade_level: `in.(${gradeLevels.join(",")})`,
              school_id: schoolId,
            },
          });
          return { data: (result.data as any[]) || [] };
        } catch {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get rooms for scheduling
     * Replaces: scheduleDataAdapter.fetchRooms()
     */
    getRoomsForScheduling: builder.query<any[], { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "structure_rooms",
            params: {
              select:
                "id, name, room_number, school_id, class_uuid, is_classroom, is_multi_class_bookable",
              school_id: schoolId,
              order: "name",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get days for scheduling
     * Replaces: scheduleDataAdapter.fetchDays()
     */
    getDaysForScheduling: builder.query<any[], { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "structure_days",
            params: {
              select: "id, name_de, name_en, day_number",
              order: "day_number",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get periods for scheduling
     * Replaces: scheduleDataAdapter.fetchPeriods()
     */
    getPeriodsForScheduling: builder.query<any[], { schoolId: string }>({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "schedule_periods",
            params: {
              select:
                "id, label, start_time, end_time, block_number, block_type",
              order: "block_number",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get semesters for scheduling (live schedule semester switcher)
     * Table: structure_school_semesters
     */
    getSemestersForScheduling: builder.query<
      Array<{ id: string; name: string; start_date: string; end_date: string }>,
      { schoolId: string }
    >({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            type: "select",
            table: "structure_school_semesters",
            params: {
              select: "id, name, start_date, end_date",
              school_id: schoolId,
              order: "start_date.desc",
            },
          });

          return { data: (result.data as any[]) || [] };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable"],
    }),

    /**
     * Get class teachers mapping
     * Replaces: scheduleDataAdapter.fetchClassTeachers()
     */
    getClassTeachersForScheduling: builder.query<
      Array<{ class_id: string; teacher_name: string }>,
      { schoolId: string }
    >({
      queryFn: async ({ schoolId }, _api, _extraOptions, baseQuery) => {
        try {
          // Get staff class links directly — school_id filter handles scoping
          const linksResult = await baseQuery({
            type: "select",
            table: "staff_class_links",
            params: {
              select: "class_id, staff_id, is_class_teacher, role",
              school_id: schoolId,
            },
          });

          if (linksResult.error || !linksResult.data) {
            return { data: [] };
          }

          const links = (linksResult.data as any[]).filter(
            (l: any) =>
              l.is_class_teacher === true ||
              ["Klassenleitung", "class_lead", "main"].includes(l.role),
          );

          if (links.length === 0) {
            return { data: [] };
          }

          // Get staff profiles
          const staffIds = Array.from(
            new Set(links.map((l: any) => l.staff_id)),
          );
          const profilesResult = await baseQuery({
            type: "select",
            table: "user_profiles",
            params: {
              select: "id, first_name, last_name",
              id: staffIds,
              school_id: schoolId,
            },
          });

          if (profilesResult.error || !profilesResult.data) {
            return { data: [] };
          }

          const profiles = profilesResult.data as any[];
          const profileMap = new Map(
            profiles.map((p: any) => [
              p.id,
              `${p.first_name || ""} ${p.last_name || ""}`.trim(),
            ]),
          );

          // Build class to teacher pairs (first class teacher encountered)
          const classTeacherMap = new Map<string, string>();
          links.forEach((link: any) => {
            if (!classTeacherMap.has(link.class_id)) {
              const teacherName = profileMap.get(link.staff_id);
              if (teacherName) {
                classTeacherMap.set(link.class_id, teacherName);
              }
            }
          });
          const pairs = Array.from(classTeacherMap.entries()).map(
            ([class_id, teacher_name]) => ({ class_id, teacher_name }),
          );

          return { data: pairs };
        } catch (error) {
          return { data: [] };
        }
      },
      providesTags: ["Timetable", "Staff"],
    }),

    /**
     * Duplicate a schedule draft to a new semester
     * Copies all draft_schedule_items to a new draft
     */
    duplicateScheduleDraft: builder.mutation<
      string,
      {
        p_source_draft_id: string;
        p_target_semester_id: string;
        p_new_title?: string;
        p_created_by: string;
      }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "duplicate_schedule_draft_react",
        params,
      }),
      invalidatesTags: ["Timetable"],
    }),

    /**
     * Update a staff member's colour in profile_info_staff
     */
    updateStaffColour: builder.mutation<
      any,
      {
        profileId: string;
        colour: string;
        schoolId: string;
      }
    >({
      queryFn: async (
        { profileId, colour, schoolId },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        try {
          const result = await baseQuery({
            type: "update",
            table: "profile_info_staff",
            params: {
              set: { colour, updated_at: new Date().toISOString() },
              match: { profile_id: profileId, school_id: schoolId },
            },
          });
          return { data: result.data };
        } catch (error) {
          return { error: { status: 500, data: error } as any };
        }
      },
      invalidatesTags: ["Staff"],
    }),

    /**
     * Create an entry in course_list (Step 1 of course creation)
     * Resolves semester dates from the draft → semester to set start_date/end_date.
     * Returns the new course_id.
     */
    createCourseListEntry: builder.mutation<
      { id: string },
      {
        school_id: string;
        draft_id: string;
        name: string;
        emoticon?: string | null;
        is_for_year_g?: number[];
        max_students?: number | null;
        subject_id?: string | null;
      }
    >({
      queryFn: async (params, _api, _extraOptions, baseQuery) => {
        try {
          // Step 0: Resolve semester dates from draft → semester
          let startDate: string | null = null;
          let endDate: string | null = null;

          const draftResult = await baseQuery({
            type: "select",
            table: "schedule_drafts",
            params: {
              select: "semester_id",
              id: params.draft_id,
              single: true,
            },
          });
          const draftData = draftResult.data as any;
          if (draftData?.semester_id) {
            const semResult = await baseQuery({
              type: "select",
              table: "structure_school_semesters",
              params: {
                select: "start_date,end_date",
                id: draftData.semester_id,
                single: true,
              },
            });
            const semData = semResult.data as any;
            if (semData) {
              startDate = semData.start_date;
              endDate = semData.end_date;
            }
          }

          // Step 1: Insert into course_list with semester dates
          const { draft_id: _draftId, ...insertFields } = params;
          const insertResult = await baseQuery({
            type: "insert",
            table: "course_list",
            params: {
              data: {
                ...insertFields,
                start_date: startDate,
                end_date: endDate,
              },
            },
          });
          if (insertResult.error) return { error: insertResult.error };

          // Step 2: Fetch the newly created row to get the ID
          const selectResult = await baseQuery({
            type: "select",
            table: "course_list",
            params: {
              select: "id",
              school_id: params.school_id,
              name: params.name,
              order: "created_at.desc",
              limit: 1,
              single: true,
            },
          });
          if (selectResult.error) return { error: selectResult.error };
          return { data: selectResult.data as { id: string } };
        } catch (error) {
          return { error: { status: 500, data: error } as any };
        }
      },
    }),

    /**
     * Get course_list details by ID via RPC
     * Used to pre-populate the course editor with fields
     * not stored on draft_schedule_items (grades, max_students, emoticon)
     */
    getCourseById: builder.query<
      {
        id: string;
        name: string;
        emoticon?: string | null;
        is_for_year_g?: number[] | null;
        max_students?: number | null;
        subject_id?: string | null;
      } | null,
      { courseId: string }
    >({
      query: ({ courseId }) => ({
        type: "rpc",
        rpc: "fn_courses_get_by_id",
        params: { p_course_id: courseId },
      }),
      providesTags: ["Timetable"],
    }),



    /**
     * Fetch active courses from course_list for "Bestehender Kurs" picker.
     * Returns id, name, emoticon, subject_id, is_for_year_g.
     */
    getActiveCourseList: builder.query<
      {
        id: string;
        name: string;
        emoticon: string | null;
        subject_id: string | null;
        is_for_year_g: number[] | null;
      }[],
      { school_id: string }
    >({
      query: (params) => ({
        type: "select" as const,
        table: "course_list",
        params: {
          select:
            "id,name,emoticon,subject_id,is_for_year_g",
          school_id: params.school_id,
          is_active: true,
          order: "name.asc",
        },
      }),
      providesTags: ["Timetable"],
    }),
  }),
});

export const {
  useGetInitialSchedulingPayloadQuery,
  useCreateScheduleDraftMutation,
  useGetDraftLessonsQuery,
  useGetStaffForSchedulingQuery,
  useCreateScheduleItemMutation,
  useUpdateScheduleItemMutation,
  useDeleteScheduleItemMutation,
  useSaveDraftVersionMutation,
  usePublishLessonsDraftMutation,
  useLazyGetPublishCalendarOverrideSummaryQuery,
  useGetLastEditedDraftQuery,
  useGetDraftsListQuery,
  useGetDraftByIdQuery,
  useGetLiveSchedulesQuery,
  useGetClassesForSchedulingQuery,
  useGetSubjectsForSchedulingQuery,
  useGetRoomsForSchedulingQuery,
  useGetDaysForSchedulingQuery,
  useGetPeriodsForSchedulingQuery,
  useGetSemestersForSchedulingQuery,
  useGetClassTeachersForSchedulingQuery,
  useDuplicateScheduleDraftMutation,
  useUpdateStaffColourMutation,
  useCreateCourseListEntryMutation,
  useGetCourseByIdQuery,
  useGetActiveCourseListQuery,
  useGetSubjectClassHoursQuery,
  useGetSubjectGradeHoursQuery,
} = schedulingApi;
